// Phase 6 · Child Health Records & Immunization Tracking client library.
// Mirrors lib/compliance.js patterns and signatures so the two compliance
// surfaces stay symmetrical.

import { supabase, isSupabaseConfigured } from "@/lib/supabase";

const BUCKET = "child-health-records";
const SIGNED_URL_TTL = 60 * 60;

const TYPE_SELECT = `
  id, organization_id, code, label, description,
  default_validity_months, is_required,
  blocks_attendance_when_missing,
  applies_age_min_months, applies_age_max_months,
  location_ids, program_keys, jurisdiction_label, source_reference,
  sort_order, active, created_at, updated_at
`;
const RECORD_SELECT = `
  id, organization_id, child_id, family_id, record_type_id,
  issued_on, expires_on, document_path, notes,
  uploaded_by_role, uploaded_by,
  revoked_at, revoked_by, revoke_reason,
  created_at, updated_at,
  record_type:child_health_record_types(id, code, label, is_required, default_validity_months,
    applies_age_min_months, applies_age_max_months, location_ids, program_keys,
    jurisdiction_label, source_reference),
  child:children(id, first_name, last_name, date_of_birth, classroom_id),
  family:families(id, display_name)
`;

// ---------- catalog ----------

export async function listChildHealthRecordTypes(organizationId, { includeInactive = false } = {}) {
  if (!isSupabaseConfigured || !organizationId) return [];
  let q = supabase
    .from("child_health_record_types")
    .select(TYPE_SELECT)
    .eq("organization_id", organizationId)
    .order("sort_order", { ascending: true });
  if (!includeInactive) q = q.eq("active", true);
  const { data, error } = await q;
  if (error) {
    if (error.code === "42P01") return [];
    throw error;
  }
  return data || [];
}

// ---------- records ----------

export async function listChildHealthRecordsForOrg(organizationId) {
  if (!isSupabaseConfigured || !organizationId) return [];
  const { data, error } = await supabase
    .from("child_health_records")
    .select(RECORD_SELECT)
    .eq("organization_id", organizationId)
    .order("expires_on", { ascending: true, nullsFirst: false });
  if (error) {
    if (error.code === "42P01") return [];
    throw error;
  }
  return data || [];
}

export async function listChildHealthRecordsForChildren(childIds) {
  if (!isSupabaseConfigured || !childIds?.length) return [];
  const { data, error } = await supabase
    .from("child_health_records")
    .select(RECORD_SELECT)
    .in("child_id", childIds)
    .order("expires_on", { ascending: true, nullsFirst: false });
  if (error) {
    if (error.code === "42P01") return [];
    throw error;
  }
  return data || [];
}

export async function upsertChildHealthRecord({
  id, childId, recordTypeId, issuedOn, expiresOn, documentPath, notes,
}) {
  if (!isSupabaseConfigured) throw new Error("Requires live Supabase");
  const { data, error } = await supabase.rpc("rpc_upsert_child_health_record", {
    p_id:             id || null,
    p_child_id:       childId,
    p_record_type_id: recordTypeId,
    p_issued_on:      issuedOn,
    p_expires_on:     expiresOn || null,
    p_document_path:  documentPath || null,
    p_notes:          notes || null,
  });
  if (error) throw error;
  return Array.isArray(data) ? data[0] || null : data;
}

export async function revokeChildHealthRecord(id, reason) {
  if (!isSupabaseConfigured) throw new Error("Requires live Supabase");
  const { data, error } = await supabase.rpc("rpc_revoke_child_health_record", {
    p_id: id, p_reason: reason || null,
  });
  if (error) throw error;
  return Array.isArray(data) ? data[0] || null : data;
}

// ---------- documents ----------

export async function uploadChildHealthDocument({ recordId, file }) {
  if (!isSupabaseConfigured) throw new Error("Requires live Supabase");
  if (!file) throw new Error("Pick a file");
  const ext = (file.name.split(".").pop() || "bin").toLowerCase().slice(0, 10);
  const path = `${recordId}/${crypto.randomUUID()}.${ext}`;
  const { error: upErr } = await supabase.storage.from(BUCKET).upload(path, file, {
    cacheControl: "3600",
    upsert: false,
  });
  if (upErr) throw upErr;
  const { error: updErr } = await supabase
    .from("child_health_records")
    .update({ document_path: path })
    .eq("id", recordId);
  if (updErr) {
    await supabase.storage.from(BUCKET).remove([path]).catch(() => {});
    throw updErr;
  }
  return path;
}

export async function getChildHealthDocumentUrl(path) {
  if (!isSupabaseConfigured || !path) return null;
  const { data, error } = await supabase
    .storage.from(BUCKET).createSignedUrl(path, SIGNED_URL_TTL);
  if (error) return null;
  return data?.signedUrl || null;
}

// ---------- helpers ----------

export const STATUS_META = {
  valid:         { label: "Current",       tone: "border-emerald-200 bg-emerald-50 text-emerald-800" },
  expiring_soon: { label: "Expiring soon", tone: "border-amber-300 bg-amber-50 text-amber-900" },
  expired:       { label: "Overdue",       tone: "border-[#C6851E]/40 bg-[#C6851E]/10 text-[#8A5A12]" },
  revoked:       { label: "Withdrawn",     tone: "border-neutral-300 bg-neutral-100 text-neutral-700" },
  missing:       { label: "Action needed", tone: "border-[#C6851E]/40 bg-[#C6851E]/10 text-[#8A5A12]" },
};

export function deriveStatus(record) {
  if (record.revoked_at) return "revoked";
  if (!record.expires_on) return "valid";
  const today = new Date(); today.setHours(0,0,0,0);
  const exp = new Date(`${record.expires_on}T00:00:00`);
  if (exp <= today) return "expired";
  const t30 = new Date(today); t30.setDate(t30.getDate() + 30);
  if (exp <= t30) return "expiring_soon";
  return "valid";
}

export function daysUntilExpiry(expiresOn) {
  if (!expiresOn) return null;
  const today = new Date(); today.setHours(0,0,0,0);
  const exp = new Date(`${expiresOn}T00:00:00`);
  return Math.round((exp - today) / 86400000);
}

export function suggestExpiryDate(issuedOn, defaultValidityMonths) {
  if (!issuedOn || !defaultValidityMonths) return "";
  const d = new Date(`${issuedOn}T00:00:00`);
  d.setMonth(d.getMonth() + defaultValidityMonths);
  return d.toISOString().slice(0, 10);
}

export function ageInMonths(birthdate) {
  if (!birthdate) return null;
  const today = new Date();
  const dob = new Date(`${birthdate}T00:00:00`);
  return (today.getFullYear() - dob.getFullYear()) * 12 + (today.getMonth() - dob.getMonth());
}

/**
 * A requirement can be scoped by an administrator to a location and/or a
 * classroom program key. An incomplete classroom assignment is deliberately
 * surfaced as a center configuration task, not as a missing family record.
 */
export function requirementScopeStatus(type, child) {
  if (!type || !child) return "applicable";
  const months = ageInMonths(child.date_of_birth);
  if (months != null) {
    if (months < (type.applies_age_min_months ?? 0)) return "not_applicable";
    if (type.applies_age_max_months != null && months > type.applies_age_max_months) {
      return "not_applicable";
    }
  }

  const locationIds = type.location_ids || [];
  const programKeys = type.program_keys || [];
  if (locationIds.length === 0 && programKeys.length === 0) return "applicable";

  const classroom = child.classroom || {};
  if (!child.classroom_id) return "configuration_needed";
  if (locationIds.length > 0) {
    if (!classroom.location_id) return "configuration_needed";
    if (!locationIds.includes(classroom.location_id)) return "not_applicable";
  }
  if (programKeys.length > 0) {
    const key = classroom.compliance_program_key?.trim().toLowerCase();
    if (!key) return "configuration_needed";
    if (!programKeys.includes(key)) return "not_applicable";
  }
  return "applicable";
}

export function typeAppliesToChild(type, child) {
  return requirementScopeStatus(type, child) === "applicable";
}

function exemptionIsActive(exemption) {
  if (exemption?.status !== "approved") return false;
  if (!exemption.expires_on) return true;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const expires = new Date(`${exemption.expires_on}T00:00:00`);
  return !Number.isNaN(expires.getTime()) && expires > today;
}

/** For a given child, return required types missing a current record. */
export function missingRequiredForChild(types, recordsForChild, child, exemptions = []) {
  const valid = new Set(
    recordsForChild
      .filter((r) => deriveStatus(r) === "valid" || deriveStatus(r) === "expiring_soon")
      .map((r) => r.record_type_id),
  );
  const exempted = new Set(
    exemptions
      .filter(exemptionIsActive)
      .map((e) => e.record_type_id),
  );
  return types.filter(
    (t) => t.is_required && t.active && typeAppliesToChild(t, child)
      && !valid.has(t.id) && !exempted.has(t.id),
  );
}

/** Roll up records by child for the parent / admin Children tab. */
export function rollupByChild(records, types, children, exemptions = []) {
  const map = new Map();
  for (const c of children || []) {
    map.set(c.id, {
      child: c,
      records: [],
      exemptions: [],
      configuration: [],
      counts: {
        valid: 0,
        expiring_soon: 0,
        expired: 0,
        revoked: 0,
        missing: 0,
        approved_exemptions: 0,
        configuration: 0,
      },
      worst: "valid",
    });
  }
  for (const r of records) {
    const e = map.get(r.child_id);
    if (!e) continue;
    const s = deriveStatus(r);
    e.records.push({ ...r, status: s });
    e.counts[s]++;
  }
  for (const exemption of exemptions || []) {
    const e = map.get(exemption.child_id);
    if (!e) continue;
    e.exemptions.push(exemption);
    if (exemptionIsActive(exemption)) e.counts.approved_exemptions++;
  }
  for (const e of map.values()) {
    if (types?.length) {
      e.missing = missingRequiredForChild(types, e.records, e.child, e.exemptions);
      e.counts.missing = e.missing.length;
      e.configuration = types.filter(
        (t) => t.is_required && t.active
          && requirementScopeStatus(t, e.child) === "configuration_needed",
      );
      e.counts.configuration = e.configuration.length;
    } else {
      e.missing = [];
    }
    e.worst = "valid";
    if (e.counts.expired > 0) e.worst = "expired";
    else if (e.counts.missing > 0) e.worst = "missing";
    else if (e.counts.expiring_soon > 0) e.worst = "expiring_soon";
    else if (e.counts.configuration > 0) e.worst = "configuration";
    else if (e.counts.revoked > 0 && e.counts.valid === 0) e.worst = "revoked";
  }
  const order = {
    expired: 5,
    missing: 4,
    expiring_soon: 3,
    configuration: 2,
    revoked: 1,
    valid: 0,
  };
  return Array.from(map.values()).sort((a, b) => order[b.worst] - order[a.worst]);
}
